import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getPeakHourDetailedData } from 'service/service';
import moment from 'moment';
import ComputeFlatTableBase from './ComputeFlatTableBase';
import { QueryRule, FieldOption, formatCompactNumber } from 'utils/adb-common-util';

const COLUMN_TO_API_MAP: Record<string, string> = {
  'usage_usd': 'usage_usd',
  'compute_id': 'compute_id',
  'job_name': 'job_name',
  'sku_name': 'sku_name',
  'usage_start_time': 'usage_start_time',
  'run_name': 'run_name',
  'period_end_time': 'period_end_time',
  'result_state': 'result_state',
  'trigger_type': 'trigger_type',
  'termination_code': 'termination_code',
  'workspace_id': 'workspace_id',
  'workspace_name': 'workspace_name',
  'period_start_time': 'period_start_time',
  'runtime_mins': 'runtime_mins',
  'usage_unit': 'usage_unit',
  'usage_quantity': 'usage_quantity',
  'usage_end_time': 'usage_end_time',
  'job_id': 'job_id',
  'run_id': 'run_id'
};

const DEFAULT_COLUMN_NAMES = [
  'Workspace Id', 'Workspace Name', 'Job Id', 'Job Name', 'Run Id', 'Run Name', 'Compute Id', 'Sku Name', 'Usage Usd',
  'Period Start Time', 'Period End Time', 'Result State', 'Trigger Type', 'Termination Code', 'Runtime Mins',
  'Usage Unit', 'Usage Quantity', 'Usage Start Time', 'Usage End Time'
];

const getColumns = () => [
  { key: 'compute_id', name: 'Compute Id', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div title={row?.compute_id}>{row?.compute_id || '--'}</div> },
  { key: 'job_name', name: 'Job Name', minWidth: 300, sortable: true, formatter: ({ row }: any) => <div title={row?.job_name}>{row?.job_name || '--'}</div> },
  { key: 'workspace_name', name: 'Workspace Name', minWidth: 250, sortable: true, formatter: ({ row }: any) => <div title={row?.workspace_name}>{row?.workspace_name || '--'}</div> },
  { key: 'sku_name', name: 'Sku Name', minWidth: 200, sortable: true, formatter: ({ row }: any) => <div title={row?.sku_name}>{row?.sku_name || '--'}</div> },
  { key: 'usage_start_time', name: 'Usage Start Time', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.usage_start_time ? moment(row.usage_start_time).format('YYYY-MM-DD HH:mm:ss') : '--'}</div> },
  { key: 'usage_usd', name: 'Usage Usd', minWidth: 120, sortable: true, formatter: ({ row }: any) => <div>{typeof row?.usage_usd === 'number' ? `$${row.usage_usd.toFixed(2)}` : row?.usage_usd || '--'}</div> },
  { key: 'run_name', name: 'Run Name', minWidth: 350, sortable: true, formatter: ({ row }: any) => <div title={row?.run_name}>{row?.run_name || '--'}</div> },
  { key: 'period_end_time', name: 'Period End Time', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.period_end_time ? moment(row.period_end_time).format('YYYY-MM-DD HH:mm:ss') : '--'}</div> },
  {
    key: 'result_state', name: 'Result State', minWidth: 140, sortable: true, formatter: ({ row }: any) => {
      const state = row?.result_state || 'UNKNOWN';
      const colorMap: Record<string, string> = { SUCCEEDED: '#4caf50', FAILED: '#f44336' };
      const color = colorMap[state] || '#ff9800';
      return <div style={{ color, fontWeight: 500 }}>{state}</div>;
    }
  },
  { key: 'trigger_type', name: 'Trigger Type', minWidth: 120, sortable: true, formatter: ({ row }: any) => <div>{row?.trigger_type || '--'}</div> },
  {
    key: 'termination_code', name: 'Termination Code', minWidth: 160, sortable: true, formatter: ({ row }: any) => {
      const code = row?.termination_code || 'N/A';
      const colorMap: Record<string, string> = { SUCCESS: '#4caf50', 'N/A': '#999' };
      const color = colorMap[code] || '#f44336';
      return <div style={{ color }}>{code}</div>;
    }
  },
  { key: 'workspace_id', name: 'Workspace Id', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div title={row?.workspace_id}>{row?.workspace_id || '--'}</div> },
  { key: 'period_start_time', name: 'Period Start Time', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.period_start_time ? moment(row.period_start_time).format('YYYY-MM-DD HH:mm:ss') : '--'}</div> },
  { key: 'runtime_mins', name: 'Runtime Mins', minWidth: 120, sortable: true, formatter: ({ row }: any) => <div>{typeof row?.runtime_mins === 'number' ? row.runtime_mins : row?.runtime_mins || '--'}</div> },
  { key: 'usage_unit', name: 'Usage Unit', minWidth: 100, sortable: true, formatter: ({ row }: any) => <div>{row?.usage_unit || '--'}</div> },
  { key: 'usage_quantity', name: 'Usage Quantity', minWidth: 140, sortable: true, formatter: ({ row }: any) => <div>{typeof row?.usage_quantity === 'number' ? formatCompactNumber(row.usage_quantity) : row?.usage_quantity || '--'}</div> },
  { key: 'usage_end_time', name: 'Usage End Time', minWidth: 180, sortable: true, formatter: ({ row }: any) => <div>{row?.usage_end_time ? moment(row.usage_end_time).format('YYYY-MM-DD HH:mm:ss') : '--'}</div> },
  { key: 'job_id', name: 'Job Id', minWidth: 150, sortable: true, formatter: ({ row }: any) => <div>{row?.job_id || '--'}</div> },
  { key: 'run_id', name: 'Run Id', minWidth: 150, sortable: true, formatter: ({ row }: any) => <div>{row?.run_id || '--'}</div> }
];

const PeakHourTable = () => {
  const getInitialQueryFilters = useCallback((navState: any) => {
    const filters: QueryRule[] = [];
    if (navState?.filterType === 'workspace' && navState?.workspaceName) {
      filters.push({ id: '1', operator: 'AND', field: 'Workspace Name', value: navState.workspaceName });
    } else {
      filters.push({ id: '1', operator: 'AND', field: 'Workspace Name', value: 'All' });
    }
    filters.push({ id: '2', operator: 'AND', field: 'Result State', value: 'All' });
    return filters;
  }, []);

  const mapQueryRuleToApi = useCallback((rule: QueryRule, workspaceOptions: FieldOption[]) => {
    if (rule.field === "Workspace Name") {
      const match = workspaceOptions.find(opt => opt.label === rule.value);
      if (match && match.value !== 'all') return { field: 'workspace_name', value: match.value };
    } else if (rule.field === "Compute Type") {
      if (rule.value && rule.value !== 'all') return { field: 'Environment', value: rule.value };
    } else if (rule.field === "Result State") {
      if (rule.value && rule.value !== 'None' && rule.value !== 'all') return { field: 'result_state', value: rule.value };
    }
    return null;
  }, []);

  const transformRecords = (records: any[]) => records.map((r: any) => ({
    compute_id: r?.compute_id, job_name: r?.job_name, sku_name: r?.sku_name, usage_start_time: r?.usage_start_time,
    usage_usd: r?.usage_usd, run_name: r?.run_name, period_end_time: r?.period_end_time, result_state: r?.result_state,
    trigger_type: r?.trigger_type, termination_code: r?.termination_code, workspace_id: r?.workspace_id,
    period_start_time: r?.period_start_time, runtime_mins: r?.runtime_mins, usage_unit: r?.usage_unit,
    usage_quantity: r?.usage_quantity, usage_end_time: r?.usage_end_time, job_id: r?.job_id, run_id: r?.run_id,
    workspace_name: r?.workspace_name
  }));

  const location = useLocation();
  const navComputeType = location.state?.computeType;
  const isFromActiveWorkspace = location.state?.fromActiveWorkspaceKPI;
  const pageTitle = location.state?.dateLabel || "Peak Hour Details";

  const serviceFuncWrapper = useCallback((params: any) => {
    const {
      startDate, endDate, env, sector, location: loc, selectedDateInString,
      page, pageSize, sortField, sortOrder, filters, exportType
    } = params;
    return getPeakHourDetailedData({
      startDate, endDate, env, sector, location: loc, selectedDateInString,
      page, limit: pageSize, sortField, sortOrder, filters, exportType
    });
  }, []);

  return (
    <ComputeFlatTableBase
      serviceFunc={serviceFuncWrapper}
      pageTitle={pageTitle}
      breadcrumbLabel="Peak Hour Analysis"
      breadcrumbPath="/databricksADB/jobsADB"
      rootActiveTab={1}
      middleBreadcrumb={isFromActiveWorkspace ? {
        label: 'Most Active Workspace',
        path: '/databricksADB/peak-hour-details',
        state: {
          computeType: navComputeType,
          fromActiveWorkspaceKPI: true
        }
      } : undefined}
      tableType="status_overview-table"
      defaultColumnNames={DEFAULT_COLUMN_NAMES}
      getColumns={getColumns}
      columnToApiMap={COLUMN_TO_API_MAP}
      preserveFilterKey="failed_job_cost_filters"
      getInitialQueryFilters={getInitialQueryFilters}
      mapQueryRuleToApi={mapQueryRuleToApi}
      transformRecords={transformRecords}
      emptyStateMessage="No compute spend data found for the selected filters."
    />
  );
};

export default PeakHourTable;